<template>
  <div class="container-fluid timetable-maker-page">
    <date-based-page-header
      v-model="curDate"
      calendar-unit="weekly"
      today-button-label="이번 주"
      :custom-date-title="weeklyDateTitle"
    >
      <template slot="desktop-control-buttons">
        <el-button
          class="calendar"
          @click="downloadTimetableImage"
        >
          시간표 이미지 다운로드
        </el-button>
      </template>
      <template slot="mobile-control-buttons">
        <el-button
          class="calendar"
          @click="openOptionActionsheets"
        >
          시간표 설정
        </el-button>
      </template>
    </date-based-page-header>

    <div class="timetable-maker">
      <div
        ref="previewContainer"
        class="timetable-preview-container"
      >
        <TimeTablePreviewArea
          ref="imageArea"
          :style="previewTransformStyle"
          :range="range"
          :course-dates="courseDates"
          :child-courses-by-date="childCoursesByDate"
          :course-start-end-times="courseStartEndTimes"
          :logo-image-blob="logoImageBlob"
          @init="onInitChildCourse"
        />
      </div>

      <div class="card-box timetable-control responsive-visible desktop">
        <TimeTableDesktopOptionForm
          :course-dates="courseDates"
          :parent-course-categories="parentCourseCategories"
          :course-start-end-times="courseStartEndTimes"
          @onUpdateLogoImage="onUpdateLogoImage" 
        />
      </div>

      <TimeTableMobileOptionActionsheets
        ref="optionActionsheets"
        :course-dates="courseDates"
        :parent-course-categories="parentCourseCategories"
        :course-start-end-times="courseStartEndTimes"
        @onUpdateLogoImage="onUpdateLogoImage"
        @requestDownloadImage="downloadTimetableImage"
      />

      <el-dialog
        :visible.sync="isFormDialogVisible"
        custom-class="fifl-dialog content-center"
        :append-to-body="true"
        :close-on-press-escape="false"
        :close-on-click-modal="false"
        top="20px"
      >
        <div
          slot="title"
          class="dialog-header"
        >
          <h2>
            시간표 이미지
          </h2>
          <span>
            아래 이미지를 꾹- 눌러 다운로드하세요.
          </span>
        </div>

        <img
          :src="timetableImage"
          class="timetable-image"
        >
      </el-dialog>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { computedGetters } from '@/utils/vuex-helper';
import dateFns from 'date-fns';
import html2canvas from 'html2canvas';
import DateBasedPageHeader from '@/components/DateBasedPageHeader';
import ParentCourseSelectBox from '../MemberManagePage/ParentCourseSelectBox';
import TimeTablePreviewArea from './TimeTablePreviewArea';
import HourMinutes from './hourMinutes';
import { makeCategoriesForSelectBox } from '@/utils/array-helper';
import resizePageMixin from '@/mixins/resizePageMixin';
import BreakTimeEditor from './BreakTimeEditor';
import { saveAs } from '@/utils/file-saver';
import toBlob from 'canvas-to-blob';
import DayOfWeekVisibilitySelect from './DayOfWeekVisibilitySelect';
import TimeTableDesktopOptionForm from './TimeTableDesktopOptionForm';
import TimeTableMobileOptionActionsheets from './TimeTableMobileOptionActionsheets';
import { fiflboxApi } from '@/utils/api';

export default {
  name: 'TimetablePage',
  components: {
    TimeTableMobileOptionActionsheets,
    TimeTableDesktopOptionForm,
    DayOfWeekVisibilitySelect,
    BreakTimeEditor,
    TimeTablePreviewArea,
    ParentCourseSelectBox,
    DateBasedPageHeader
  },
  mixins: [resizePageMixin],
  data() {
    return {
      curDate: new Date(),
      range: {
        start: new Date(2019, 2, 25),
        end: new Date(2019, 2, 31)
      },
      logoImageBlob: null,
      downloadedImg: null,
      parentCourseCategories: [],
      previewTransformStyle: {
        transform: 'scale(0.5)',
        left: '0',
        opacity: '0'
      },
      fileList: [],
      timetableImage: null,
      isFormDialogVisible: false,
      courseStartEndTimes: [],
      courseDates: [],
      childCoursesByDate: {}
    };
  },
  watch: {
    curDate: {
      handler() {
        this.onDayChanged();
      }
    },
    childCoursesByDate() {
      this.courseStartEndTimes = [];
    },
    dayOfWeekVisible() {
      this.courseStartEndTimes = [];
    },
    invisibleParentCourses() {
      this.courseStartEndTimes = [];
    },
    isTimetableMenuVisible: {
      immediate: true,
      handler: function(isAccessAllowed) {
        if (!isAccessAllowed) {
          this.$router.push('/announce/access-block').catch(() => {});
        }
      }
    }
  },
  computed: {
    ...mapGetters(['getInstructorByIds']),
    ...computedGetters(['centerAccount', 'isTimetableMenuVisible']),
    ...computedGetters(
      ['dayOfWeekVisible', 'invisibleParentCourses', 'title'],
      'timeTable'
    ),
    weeklyDateTitle() {
      const isSameMonth = dateFns.isSameMonth(this.range.start, this.range.end);

      return `${dateFns.format(this.range.start, 'YYYY년 M월 D일')} - ${
        isSameMonth
          ? dateFns.format(this.range.end, 'D일')
          : dateFns.format(this.range.end, 'M월 D일')
      }`;
    }
  },
  async mounted() {
    this.$store.dispatch('syncInstructors');
    this.syncParentCourses();
    this.curDate = new Date();
    this.onDayChanged();
    await this.$store.dispatch('syncCenterAccount');
    this.initTitle();
    this.calcPreviewContainerRatio();
    toBlob.init();
  },
  methods: {
    initTitle() {
      if (this.title === '') {
        this.$store.commit(
          'timeTable/setTitle',
          this.centerAccount.name + ' 시간표'
        );
      }
    },
    onUpdateLogoImage(image) {
      this.logoImageBlob = image;
    },
    onResize() {
      this.calcPreviewContainerRatio();
    },
    openOptionActionsheets() {
      this.$refs['optionActionsheets'].openActionsheets();
    },
    calcPreviewContainerRatio() {
      const containerRatio =
        this.$refs['previewContainer'].offsetWidth /
        this.$refs['previewContainer'].offsetHeight;
      const contentRatio = 962 / 1204;
      let scaleRatio = 1;

      if (containerRatio < contentRatio) {
        scaleRatio = this.$refs['previewContainer'].offsetWidth / 962;
      } else {
        scaleRatio = this.$refs['previewContainer'].offsetHeight / 1204;
      }

      this.previewTransformStyle.transform = `scale(${scaleRatio})`;
      this.previewTransformStyle.left =
        (this.$refs['previewContainer'].offsetWidth - 962 * scaleRatio) / 2 +
        'px';
      this.previewTransformStyle.opacity = '1';
    },
    async onDayChanged() {
      this.range.start = dateFns.addDays(dateFns.startOfWeek(this.curDate), 1);
      this.range.end = dateFns.addDays(dateFns.endOfWeek(this.curDate), 1);

      await this.syncChildCourses();
    },
    async downloadTimetableImage() {
      this.$ga.event('시간표', '다운로드');

      let imageAreaEl = document
        .getElementById('timetable-preview-area')
        .cloneNode(true);
      imageAreaEl.style.transform = 'none';
      imageAreaEl.style.position = 'absolute';
      imageAreaEl.style.left = '-999999px';
      document.body.appendChild(imageAreaEl);

      let canvas = await html2canvas(imageAreaEl, {
        scale: 3
      });

      let fileName = `${this.centerAccount.name}_${dateFns.format(
        this.range.start,
        'YYYY.MM.DD'
      )}-${dateFns.format(this.range.end, 'YYYY.MM.DD')}`;

      canvas.toBlob(blob => {
        const resultOfImage = saveAs(blob, `${fileName}.png`);

        if (resultOfImage) {
          this.timetableImage = resultOfImage;
          this.isFormDialogVisible = true;
        }
      });

      imageAreaEl.remove();
    },

    async syncChildCourses() {
      let { data } = await fiflboxApi.v2.getChildCourses({
        date_after: dateFns.format(this.range.start, 'YYYY-MM-DD'),
        date_before: dateFns.format(this.range.end, 'YYYY-MM-DD')
      });

      this.courseDates = _.sortBy(Object.keys(data));

      const mapData = new Map();

      this.courseDates.forEach(key => {
        mapData.set(key, data[key]);
      });
      this.childCoursesByDate = mapData;
    },

    async syncParentCourses() {
      let { data } = await fiflboxApi.v2.getCourseCategories({
        is_removed: false
      });

      this.parentCourseCategories = makeCategoriesForSelectBox(
        data,
        'parent_courses'
      );
    },
    onInitChildCourse({ childCourse, isVisible }) {
      isVisible =
        !this.invisibleParentCourses.includes(childCourse.parent_course.id) &&
        !this.dayOfWeekVisible.includes(
          dateFns.format(childCourse.dt, 'YYYY-MM-DD')
        );

      if (!isVisible) {
        return;
      }

      let startHourMinutes = HourMinutes.dateToMinutes(childCourse.start);
      let endHourMinutes = HourMinutes.dateToMinutes(
        dateFns.addMinutes(childCourse.start, childCourse.duration)
      );

      if (endHourMinutes === 0) {
        endHourMinutes = 24 * 60;
      }

      if (!this.courseStartEndTimes.includes(startHourMinutes)) {
        this.courseStartEndTimes.push(startHourMinutes);
      }

      if (!this.courseStartEndTimes.includes(endHourMinutes)) {
        this.courseStartEndTimes.push(endHourMinutes);
      }
    }
  }
};
</script>

<style lang="scss">
.logo-image-upload {
  margin-top: 10px;
}

.timetable-maker {
  position: relative;
  height: calc(100% - 120px);

  @media screen and (max-width: 768px) {
    height: calc(100% - 70px);
  }
}

.timetable-image {
  width: 100%;
}

.van-actionsheet {
  color: #323233;
  height: 200px;
  overflow-y: auto;

  .logo-image-upload {
    margin-top: 0px;
  }

  .van-actionsheet__cancel,
  .van-actionsheet__item {
    font-size: 15px;
    height: 46px;
    line-height: 46px;
  }

  .van-actionsheet__content {
    padding: 15px 20px;
  }

  .el-form.el-form--label-top {
    padding: 0;
  }

  .el-form-item {
    position: relative;
  }

  .el-checkbox.is-bordered {
    display: block;
  }

  .el-checkbox.is-bordered + .el-checkbox.is-bordered {
    margin-left: 0;
  }

  .el-switch {
    position: absolute;
    top: -20px;
    right: 0;
  }

  .el-checkbox-button__inner {
    color: #4a4a4a;
    background-color: #d8d8d8;
    border-color: #cecece;

    &:first-child {
      border-left: 1px solid #9e9e9e;
    }
  }

  .el-checkbox-button {
    margin: 0;
  }

  .el-checkbox-button.is-checked .el-checkbox-button__inner {
    background-color: white;
    color: #d3d3d3;
    border-color: #d3d3d3;
    box-shadow: -1px 0 0 0 #c4c4c4;
  }
}

.van-overlay {
  background-color: rgba(0, 0, 0, 0.1);
}

.timetable-maker-page {
  height: calc(100% - 36px);

  @media screen and (max-width: 768px) {
    height: calc(100vh - 150px);
  }
}

.timetable-preview-container {
  margin-right: 400px;
  height: 100%;

  @media screen and (max-width: 768px) {
    margin: 0;
  }
}

.timetable-preview-area {
  max-width: 962px;
  max-height: 1204px;
  transform: scale(0.7);
  transform-origin: top left;
  position: absolute;
  top: 0;
  left: 0;
}

.el-upload-list__item .el-icon-upload-success {
  display: none;
}

.timetable-control {
  position: absolute;
  top: 0;
  right: 0;
  width: 380px;
  -webkit-box-sizing: content-box;
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;

  .el-form-item {
    position: relative;
  }

  .el-checkbox.is-bordered {
    display: block;
  }

  .el-checkbox.is-bordered + .el-checkbox.is-bordered {
    margin-left: 0;
  }

  .el-switch {
    position: absolute;
    top: -20px;
    right: 0;
  }

  .el-checkbox-button__inner {
    color: #4a4a4a;
    background-color: #d8d8d8;
    border-color: #cecece;
    padding: 12px 14px;

    &:first-child {
      border-left: 1px solid #9e9e9e;
    }
  }

  .el-checkbox-button {
    margin: 0;
  }

  .el-checkbox-button.is-checked .el-checkbox-button__inner {
    background-color: white;
    color: #d3d3d3;
    border-color: #d3d3d3;
    box-shadow: -1px 0 0 0 #c4c4c4;
  }
}

.card-box {
  padding: 35px;
}

.this-month {
  position: absolute;
  top: 100px;
  right: 40px;
  font-size: 20px;
  letter-spacing: -0.53px;
  text-align: right;
}

.center-info {
  margin: 40px auto 0;
  text-align: center;

  font-size: 16px;
  letter-spacing: -0.36px;
  line-height: 24px;
}

.fifl-inc {
  font-size: 16px;
  letter-spacing: -0.36px;
  text-align: center;
  margin-top: 20px;
  font-weight: bold;
}

.timetable-background {
  position: relative;
  width: 962px;
  height: 1204px;
  background-color: #5d5d5d;
  padding: 40px 40px 20px;
  box-sizing: border-box;
  transform-origin: 0% 0%;
  transform: scale(1);
}

.timetable-logo {
  width: 100%;
  height: 80px;
  margin: 0 0 40px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  font-size: 40px;
  text-align: center;
  line-height: 80px;
  font-weight: 400;
  letter-spacing: -1.5px;
}

.timetable-container {
  width: 880px;
  height: 880px;
  background: rgba(255, 255, 255, 0.89);
  padding: 20px 10px 30px;
  box-sizing: border-box;
  display: flex;

  .timetable-day {
    flex: 1;
    flex-direction: column;
  }
}

.timetable__col-header {
  width: 80px;
}

.timetable__day {
  flex: 1;
  /*border-right: 1px solid #ffffff;*/
}

.day-label {
  width: 100%;
  height: 100px;
  background-color: #c2c2c2;
}

.hour {
  flex: 1;
}

.break-time__item {
  border-radius: 6px;
  background-color: #f2f2f2;
  border: solid 1px #d3d3d3;
  color: #4a4a4a;
  padding: 10px;
  box-sizing: border-box;
  position: relative;
  margin-bottom: 5px;

  .control {
    position: absolute;
    right: 15px;
    top: 11px;

    font-size: 12px;
    font-weight: 500;
    text-align: right;
    color: #9b9b9b;
  }
}

.el-form-item {
  .break-time__input {
    border-radius: 6px;
    background-color: #ffffff;
    border: solid 1px #d3d3d3;
    color: #4a4a4a;
    padding: 10px;
    box-sizing: border-box;
    position: relative;
    margin-bottom: 5px;

    .el-input {
      width: 70px;

      input.el-input__inner {
        display: inline-block;
        text-align: center;
        height: 36px;
        line-height: 15px;
        font-size: 15px;
        letter-spacing: -0.8px;
        color: #4a4a4a;
        font-weight: 400;
        border-radius: 6px;
        padding: 0 8px;
      }
    }
  }
}
</style>
